import { useQuery } from '@tanstack/react-query';

import { api } from '../api/client';
import type { components } from '../api/schema';
import { unwrap } from '../api/unwrap';
import { buildListQuerySerializer } from '../lib/list-query';

// Plan 10-03 — operator history of storage→sender shipments. Reads
// `GET /replenishment-transfers` (Plan 10-02 backend). Filters cover
// the four routing dimensions (warehouse / storage / sender / status)
// + an optional date window.

export type ReplenishmentTransferDto = components['schemas']['ReplenishmentTransfer'];
export type ReplenishmentStatus = ReplenishmentTransferDto['status'];
export const REPLENISHMENT_STATUSES: ReplenishmentStatus[] = [
  'pending',
  'sent',
  'accepted',
  'failed',
  'cancelled',
];

export interface ReplenishmentTransfersFilter {
  warehouseId?: string | undefined;
  storageAccountId?: string | undefined;
  senderAccountId?: string | undefined;
  status?: ReplenishmentStatus | undefined;
  from?: string | undefined;
  to?: string | undefined;
}

export interface ReplenishmentTransfersListInput {
  filter: ReplenishmentTransfersFilter;
  page: number;
  pageSize: number;
  sort: 'createdAt';
  dir: 'asc' | 'desc';
}

export interface ReplenishmentTransfersListResult {
  items: ReplenishmentTransferDto[];
  page: number;
  pageSize: number;
  total: number;
}

export const useReplenishmentTransfers = (input: ReplenishmentTransfersListInput) =>
  useQuery({
    queryKey: ['replenishment-transfers', 'list', input] as const,
    queryFn: async (): Promise<ReplenishmentTransfersListResult> => {
      const filter: Record<string, string> = {};
      if (input.filter.warehouseId) filter.warehouseId = input.filter.warehouseId;
      if (input.filter.storageAccountId) filter.storageAccountId = input.filter.storageAccountId;
      if (input.filter.senderAccountId) filter.senderAccountId = input.filter.senderAccountId;
      if (input.filter.status) filter.status = input.filter.status;
      if (input.filter.from) filter.from = input.filter.from;
      if (input.filter.to) filter.to = input.filter.to;

      const result = await api.GET('/api/replenishment-transfers', {
        params: {
          query: {
            sort: input.dir === 'desc' ? `-${input.sort}` : input.sort,
            page: String(input.page),
            pageSize: String(input.pageSize),
          },
        },
        querySerializer: buildListQuerySerializer(filter),
      });
      return unwrap(result) as ReplenishmentTransfersListResult;
    },
    refetchInterval: 30_000,
  });
